/**
 * @fileoverview TipTap Field Placeholder Extension
 *
 * Purpose: Renders {{field_name}} template placeholders as atomic inline chips
 * that cannot be partially edited. Users can delete them or insert new ones
 * from the available field list.
 *
 * The node stores the field `key` as an attribute, and optionally a `label`
 * for display. On HTML serialization it outputs a `<span>` with data attributes
 * that the html-utils postprocessor converts back to `{{key}}`.
 */

import { mergeAttributes, Node } from "@tiptap/core";
import { type NodeViewRendererProps } from "@tiptap/core";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { TEMPLATE_COLLECTIONS_BY_KEY } from "@cvx/jobs/precedents/collectionsShared";
import type { Editor } from "@tiptap/react";
import {
  type FieldModifier,
  modifierDecoration,
  parseModifiers,
  serializeModifiers,
} from "./field-modifiers";

export interface FieldPlaceholderOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    fieldPlaceholder: {
      insertFieldPlaceholder: (attrs: {
        key: string;
        label?: string;
        /**
         * For checkbox-type fields: which state of the linked boolean this
         * chip represents — "checked" (default) renders ☑ when the field
         * is checked, "unchecked" renders ☑ when the field is unchecked.
         * Lets a Yes/No question use one field with two chips.
         */
        checkboxMode?: "checked" | "unchecked";
        /**
         * Presentation-only enhancements applied to the resolved value at
         * render time (wrap in parens/brackets/quotes, pluralize, possessive,
         * case). Persisted as a `data-field-mods` pipe string.
         */
        modifiers?: FieldModifier[];
      }) => ReturnType;
    };
  }
}

export const FieldPlaceholder = Node.create<FieldPlaceholderOptions>({
  name: "fieldPlaceholder",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,
  // Permit all marks (bold, italic, textStyle color/font/size, etc.) on the
  // chip so toolbar formatting works even when the chip is the only thing
  // selected on a line. ProseMirror leaf inline nodes default to an empty
  // markSet — without this, setMark/toggleMark commands silently no-op.
  marks: "_",

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      key: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          element.getAttribute("data-field-key"),
        renderHTML: (attributes: Record<string, unknown>) => ({
          "data-field-key": attributes.key,
        }),
      },
      label: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          element.getAttribute("data-field-label"),
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes.label) return {};
          return { "data-field-label": attributes.label };
        },
      },
      checkboxMode: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const v = element.getAttribute("data-checkbox-mode");
          return v === "checked" || v === "unchecked" ? v : null;
        },
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes.checkboxMode) return {};
          return { "data-checkbox-mode": attributes.checkboxMode };
        },
      },
      modifiers: {
        default: [] as FieldModifier[],
        parseHTML: (element: HTMLElement) =>
          parseModifiers(element.getAttribute("data-field-mods")),
        renderHTML: (attributes: Record<string, unknown>) => {
          const mods = (attributes.modifiers as FieldModifier[]) ?? [];
          if (!mods.length) return {};
          return { "data-field-mods": serializeModifiers(mods) };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="field-placeholder"]',
      },
    ];
  },

  renderHTML({
    node,
    HTMLAttributes,
  }: {
    node: ProseMirrorNode;
    HTMLAttributes: Record<string, unknown>;
  }) {
    const label =
      (node.attrs.label as string) ||
      formatKeyAsLabel(node.attrs.key as string);
    return [
      "span",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        "data-type": "field-placeholder",
        class: "field-placeholder-chip",
      }),
      label,
    ];
  },

  addCommands() {
    return {
      insertFieldPlaceholder:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs,
          });
        },
    };
  },

  addNodeView() {
    return ({ node, editor, getPos }: NodeViewRendererProps) => {
      const dom = document.createElement("span");
      dom.classList.add("field-placeholder-chip");
      dom.setAttribute("data-type", "field-placeholder");
      dom.setAttribute("data-field-key", node.attrs.key as string);
      const mode = node.attrs.checkboxMode as string | null;
      const isCheckbox = mode === "checked" || mode === "unchecked";
      if (isCheckbox) {
        dom.setAttribute("data-checkbox-mode", mode);
      }
      const mods = parseModifiers(
        serializeModifiers((node.attrs.modifiers as FieldModifier[]) ?? []),
      );
      if (mods.length) {
        dom.setAttribute("data-field-mods", serializeModifiers(mods));
      }
      dom.contentEditable = "false";
      const baseLabel =
        deriveDisplayLabel(node, editor as Editor, getPos) ??
        ((node.attrs.label as string) ||
          formatKeyAsLabel(node.attrs.key as string));
      // For checkbox chips, prefix with a box glyph so the editor visually
      // signals "this is a checkbox placeholder" — and append a small
      // suffix when the chip is wired to the unchecked state.
      if (isCheckbox) {
        const glyph = document.createElement("span");
        glyph.textContent = "☐";
        glyph.style.marginRight = "4px";
        dom.appendChild(glyph);
        dom.appendChild(document.createTextNode(baseLabel));
        if (mode === "unchecked") {
          const suffix = document.createElement("span");
          suffix.textContent = " (unchecked)";
          suffix.style.opacity = "0.6";
          suffix.style.marginLeft = "2px";
          dom.appendChild(suffix);
        }
      } else {
        // Decorate the label so the author sees the enhancement: wrap chars
        // hug the label for parens/brackets/quotes; grammatical/case mods show
        // as small muted badges after it. Shared with the static preview
        // renderer via modifierDecoration so editor and preview stay in sync.
        const { open, close, badges } = modifierDecoration(mods);
        dom.appendChild(document.createTextNode(`${open}${baseLabel}${close}`));
        for (const text of badges) {
          const badge = document.createElement("span");
          badge.textContent = text;
          badge.style.opacity = "0.6";
          badge.style.fontSize = "0.75em";
          badge.style.marginLeft = "3px";
          dom.appendChild(badge);
        }
      }
      return { dom };
    };
  },
});

function formatKeyAsLabel(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * English singularisation for the collection labels we ship (e.g.
 * "Directors" → "Director", "Signatories" → "Signatory"). Kept in sync
 * with the sibling helper in `insert-collection-dropdown.tsx` so freshly
 * inserted chips and re-rendered legacy chips read identically.
 */
function singularise(noun: string): string {
  if (noun.endsWith("ies")) return `${noun.slice(0, -3)}y`;
  if (noun.endsWith("s")) return noun.slice(0, -1);
  return noun;
}

/**
 * Compute the display label for a placeholder chip, ignoring any stale
 * `label` attr saved from earlier template versions. Returns `null` when
 * the NodeView should defer to the stored label.
 *
 * Only `item_<part>` chips are derived here — they must resolve against
 * the enclosing `repeatBlock`'s collection, which is only available at
 * NodeView time. Contact chips (`contact_job_*`, `contact_firm_*`) rely
 * on their stored label because the template's tagged-role context
 * (needed for `Director: Name` vs `Job Contact: Name`) is baked in
 * upstream by `refreshFieldChipLabels` in `html-utils.ts`.
 */
function deriveDisplayLabel(
  node: ProseMirrorNode,
  editor: Editor,
  getPos: () => number | undefined,
): string | null {
  const key = node.attrs.key as string | null;
  if (!key || !key.startsWith("item_")) return null;

  const pos = getPos();
  if (pos === undefined) return null;
  const collectionKey: string | null = (() => {
    try {
      const $pos = editor.state.doc.resolve(pos);
      for (let depth = $pos.depth; depth > 0; depth -= 1) {
        const parent = $pos.node(depth);
        if (parent.type.name === "repeatBlock") {
          return (parent.attrs.collection as string | null) ?? null;
        }
      }
      return null;
    } catch {
      return null;
    }
  })();
  if (!collectionKey) return null;
  const meta = TEMPLATE_COLLECTIONS_BY_KEY.get(collectionKey);
  if (!meta) return null;
  const part = key.slice("item_".length);
  const fieldMeta = meta.fields.find((f) => f.part === part);
  const fieldLabel = fieldMeta?.label ?? formatKeyAsLabel(part);
  return `${singularise(meta.label)}: ${fieldLabel}`;
}
